import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, Package, ShoppingCart, TrendingUp, ChevronDown, ChevronUp, Car, ArrowUpDown, ArrowUp, ArrowDown, Download, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const SORT_COLUMNS = [
  { key: "nom", label: "Piece" },
  { key: "total_achats", label: "Nb achats" },
  { key: "prix_moyen", label: "Prix moyen" },
  { key: "prix_min", label: "Prix min" },
  { key: "prix_max", label: "Prix max" },
  { key: "total_depense", label: "Total depense" },
];

export default function InvestorPiecesPage() {
  const [stats, setStats] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [sortKey, setSortKey] = useState("total_achats");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, vRes] = await Promise.all([
          api.get("/investor/pieces"), api.get("/vehicles")
        ]);
        setStats(sRes.data);
        setVehicles(vRes.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const getVehicleName = (id) => {
    const v = vehicles.find(vh => vh.id === id);
    return v ? `${v.immatriculation} - ${v.marque} ${v.modele}` : "-";
  };

  const toggleExpand = (nom) => setExpanded(prev => ({ ...prev, [nom]: !prev[nom] }));

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "nom" ? "asc" : "desc");
    }
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3 h-3 text-slate-300 ml-1" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-[#0F52BA] ml-1" /> : <ArrowDown className="w-3 h-3 text-[#0F52BA] ml-1" />;
  };

  const filtered = stats
    .filter(p => p.nom.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let valA = a[sortKey], valB = b[sortKey];
      if (sortKey === "nom") {
        valA = valA.toLowerCase(); valB = valB.toLowerCase();
        return sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return sortDir === "asc" ? valA - valB : valB - valA;
    });

  const totalPieces = stats.reduce((s, p) => s + p.total_achats, 0);
  const totalDepense = stats.reduce((s, p) => s + p.total_depense, 0);
  const piecesUniques = stats.length;

  const invPiecesExportCols = [
    { label: "Piece", key: "nom" },
    { label: "Nb achats", key: "total_achats" },
    { label: "Prix moyen", key: "prix_moyen" },
    { label: "Prix min", key: "prix_min" },
    { label: "Prix max", key: "prix_max" },
    { label: "Total depense", key: "total_depense" },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="investor-pieces-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pieces achetees</h1>
          <p className="text-sm text-slate-500 mt-1">{piecesUniques} piece(s) differente(s), {totalPieces} achat(s) au total</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="investor-pieces-stats">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Package className="w-4 h-4 text-blue-600" /></div></div>
          <p className="text-xl font-bold text-blue-600 font-heading">{piecesUniques}</p>
          <p className="text-xs text-slate-500">Pieces differentes</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><ShoppingCart className="w-4 h-4 text-emerald-600" /></div></div>
          <p className="text-xl font-bold text-emerald-600 font-heading">{totalPieces}</p>
          <p className="text-xs text-slate-500">Achats au total</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-amber-600" /></div></div>
          <p className="text-xl font-bold text-amber-600 font-heading">{totalDepense.toLocaleString("fr-FR")} Frc</p>
          <p className="text-xs text-slate-500">Depense totale en pieces</p>
        </div>
      </div>

      {/* Search + Export */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher une piece..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="input-search-piece" />
        </div>
        <div className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-export-inv-pieces">
                <Download className="w-4 h-4 mr-2" />Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCSV(filtered, invPiecesExportCols, "pieces-investisseur")} data-testid="export-inv-pieces-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel(filtered, invPiecesExportCols, "pieces-investisseur")} data-testid="export-inv-pieces-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF(filtered, invPiecesExportCols, "pieces-investisseur", "Pieces achetees")} data-testid="export-inv-pieces-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600 w-8"></TableHead>
              {SORT_COLUMNS.map(col => (
                <TableHead key={col.key} className="font-semibold text-slate-600">
                  <button type="button" className="flex items-center hover:text-[#0F52BA] transition-colors" onClick={() => handleSort(col.key)} data-testid={`sort-${col.key}`}>
                    {col.label}<SortIcon colKey={col.key} />
                  </button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(p => {
              const isExpanded = expanded[p.nom];
              return (
                <React.Fragment key={p.nom}>
                  <TableRow className="cursor-pointer hover:bg-slate-50/80" onClick={() => toggleExpand(p.nom)} data-testid={`piece-row-${p.nom}`}>
                    <TableCell className="w-8 px-2">
                      {p.achats.length > 0 && (
                        <div className="text-slate-400">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{p.nom}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-semibold">{p.total_achats}x</Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{p.prix_moyen} Frc</TableCell>
                    <TableCell className="text-slate-500 text-sm">{p.prix_min} Frc</TableCell>
                    <TableCell className="text-slate-500 text-sm">{p.prix_max} Frc</TableCell>
                    <TableCell className="font-semibold text-amber-700">{p.total_depense.toLocaleString("fr-FR")} Frc</TableCell>
                  </TableRow>
                  {isExpanded && p.achats.map((a, i) => (
                    <TableRow key={`${p.nom}-${i}`} className="bg-blue-50/30 border-l-2 border-blue-300" data-testid={`achat-row-${p.nom}-${i}`}>
                      <TableCell></TableCell>
                      <TableCell colSpan={2} className="text-sm">
                        <div className="flex items-center gap-2 text-blue-700">
                          <Car className="w-3.5 h-3.5" />
                          <span>{getVehicleName(a.vehicule_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-blue-700 font-medium text-sm">{a.prix} Frc</TableCell>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell className="text-slate-400 text-xs">{a.date ? new Date(a.date + "T12:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                  <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  {search ? `Aucune piece trouvee pour "${search}"` : "Aucune piece achetee pour vos vehicules"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
